import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useCallback, useMemo } from 'react';

import {
  type InteriorHandoffPhoto,
  saveInteriorHandoff,
} from '@/lib/interiorHandoff';
import { signupHrefForPath } from '@/lib/presetFromPath';

import { IMAGES_IN_PLAN } from '@/constants/common';
import type { InteriorMode } from '@/constants/interiorDesigner';
import { nanoBananaProCredits } from '@/constants/nanoBananaCredits';
import { useAnalytics } from '@/contexts/Analytics';
import { useUser } from '@/utils/useUser';

import { INTERIOR_STYLE_COUNT } from './content';
import { INTERIOR_ROOMS } from './rooms';
import type { InteriorLandingPage } from './types';

/** Credits a design costs at the default 2K size (the same at 1K). */
export const CREDITS_PER_DESIGN = nanoBananaProCredits('2K');

export const designsFor = (credits: number) =>
  Math.floor(credits / CREDITS_PER_DESIGN);

const interpolate = (text: string, query?: Record<string, unknown>) =>
  query
    ? text.replace(/{{\s*(\w+)\s*}}/g, (match, name: string) =>
        name in query ? String(query[name]) : match,
      )
    : text;

/**
 * The interior landing pages' `t` (namespace `interior-landing`). A key a
 * locale hasn't translated yet falls back to the English key with its
 * {{placeholders}} filled in (next-translate otherwise shows a missing key
 * raw, placeholders and all).
 */
export function useInteriorLandingT() {
  const { t, lang } = useLocale('interior-landing');
  const translate = useCallback(
    (key: string, query?: Record<string, unknown>) =>
      // English is the key itself: skip the lookup (no English file, and no
      // missing-key warning per string in development).
      lang === 'en'
        ? interpolate(key, query)
        : t(key, query, { default: interpolate(key, query) }),
    [lang, t],
  );
  return { t: translate, lang };
}

export type InteriorT = ReturnType<typeof useInteriorLandingT>['t'];

/**
 * {{room}} and {{room_lower}} for a room name (an English key), so a
 * translation can use whichever its grammar needs.
 */
export function useRoomVars() {
  const { t, lang } = useInteriorLandingT();
  return useCallback(
    (name: string) => {
      const room = t(name);
      return { room, room_lower: roomLower(room, lang) };
    },
    [lang, t],
  );
}

/**
 * A room name for the middle of a sentence ("your walk-in closet"). German
 * keeps its capitalised nouns; acronyms (ADU) keep their capitals.
 */
export function roomLower(name: string, lang: string) {
  if (lang === 'de') return name;
  return name
    .split(' ')
    .map((word) =>
      word.length > 1 && word === word.toLocaleUpperCase(lang)
        ? word
        : word.toLocaleLowerCase(lang),
    )
    .join(' ');
}

/**
 * Values for a page's {{placeholders}}: its own vars, translated (a room
 * page's room name, also as {{room_lower}}), plus {{styles}}, {{rooms}},
 * {{credits}} (per design) and {{starter}} (designs a month on Starter).
 */
export function usePageVars(page: Pick<InteriorLandingPage, 'vars'>) {
  const { t, lang } = useInteriorLandingT();
  return useMemo(() => {
    const vars: Record<string, unknown> = {
      styles: INTERIOR_STYLE_COUNT,
      rooms: INTERIOR_ROOMS.length,
      credits: CREDITS_PER_DESIGN,
      starter: designsFor(IMAGES_IN_PLAN.Starter),
    };
    for (const [key, value] of Object.entries(page.vars ?? {})) {
      const text = t(value);
      vars[key] = text;
      vars[`${key}_lower`] = roomLower(text, lang);
    }
    return vars;
  }, [lang, page.vars, t]);
}

export type StartSurface =
  | 'upload'
  | 'sample'
  | 'scratch'
  | 'color'
  | 'style'
  | 'idea'
  | 'mode'
  | 'cta';

export interface StartOptions {
  surface: StartSurface;
  photo?: InteriorHandoffPhoto;
  mode?: InteriorMode;
  roomType?: string;
  style?: string;
  prompt?: string;
}

/**
 * Opens the AI Interior Designer from a landing page: saves what the visitor
 * picked (see @/lib/interiorHandoff), then goes to the tool, or to sign-up
 * first, which hands over to the tool afterwards.
 */
export function useStartInterior(page: InteriorLandingPage) {
  const router = useRouter();
  const analytics = useAnalytics();
  const { user } = useUser();

  return useCallback(
    async (options: StartOptions) => {
      const mode = options.mode ?? page.mode;
      const style = options.style ?? page.style;
      const saved = await saveInteriorHandoff({
        source: page.id,
        mode,
        roomType: options.roomType ?? page.roomType,
        style,
        prompt: options.prompt,
        photo: options.photo,
      });
      analytics.track('Interior Landing Start', {
        page: page.id,
        surface: options.surface,
        mode,
        photo: options.photo?.kind ?? 'none',
        style,
        signed_in: Boolean(user),
        saved,
      });
      await router.push(
        user ? '/ai-interior-designer' : signupHrefForPath(router.asPath),
      );
    },
    [analytics, page.id, page.mode, page.roomType, page.style, router, user],
  );
}

/** Where plain "Start" links go (no hand-off needed). */
export function useStartHref() {
  const router = useRouter();
  const { user } = useUser();
  return user ? '/ai-interior-designer' : signupHrefForPath(router.asPath);
}
